/* eslint-disable ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
// Extends From
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember Imports
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { pluralize } from '@linc-technologies/linced-common/utils/inflector';
// External Imports
import moment from 'moment';
// Internal Imports
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import {
  filterBy,
  findBy,
  rejectBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class LogController extends Controller {
  @service
  terms;

  @service
  toaster;

  @tracked
  dateFilter = 'day';
  @tracked
  filters = [];
  @tracked scheduleFilter = '';
  @tracked
  shortDescriptionFilter = '';
  @tracked
  deleteDialogOpen = false;
  @tracked endDate = moment().format('YYYY-MM-DD');
  @tracked startDate = moment().format('YYYY-MM-DD');
  @tracked
  selected = [];

  @computed(
    'model.logs.[]',
    'filters.@each.{field,value}',
    'startDate',
    'endDate',
    'scheduleFilter',
  )
  get filteredLogs() {
    let availableLogs = this.model.logs.filter((log) => !log.deleted);
    if (
      isEmpty(this.filters) &&
      isEmpty(this.startDate) &&
      isEmpty(this.endDate) &&
      isEmpty(this.scheduleFilter)
    ) {
      // No filters - just return the data.
      return availableLogs;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    let logs = availableLogs.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });

    if (!isEmpty(this.startDate)) {
      logs = logs.filter((log) => {
        return log.displayDate > moment(this.startDate, 'YYYY-MM-DD').unix();
      });
    }
    if (!isEmpty(this.endDate)) {
      logs = logs.filter((log) => {
        return (
          log.displayDate <
          moment(this.endDate, 'YYYY-MM-DD').add(1, 'day').unix()
        );
      });
    }

    if (!isEmpty(this.scheduleFilter)) {
      if (this.scheduleFilter === 'scheduled') {
        logs = filterBy(logs, 'schedule_id');
      }
      if (this.scheduleFilter === 'not-scheduled') {
        logs = rejectBy(logs, 'schedule_id');
      }
    }

    return logs;
  }

  clearSelection() {
    this.selected = [];
  }

  @action
  filter(field, value) {
    if (field === 'medication.shortDescription') {
      this.shortDescriptionFilter = value;
    }

    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  setStartDate(value) {
    this.startDate = value;
  }

  @action
  setEndDate(value) {
    this.endDate = value;
  }

  @action
  setDateFilter(value) {
    if (value === this.dateFilter) {
      this.dateFilter = '';
      this.startDate = '';
      this.endDate = '';
    } else {
      this.dateFilter = value;
    }
    if (this.dateFilter === 'day') {
      this.startDate = moment().format('YYYY-MM-DD');
      this.endDate = moment().format('YYYY-MM-DD');
    }
    if (this.dateFilter === 'week') {
      this.startDate = moment().startOf('week').format('YYYY-MM-DD');
      this.endDate = moment().endOf('week').format('YYYY-MM-DD');
    }
    if (this.dateFilter === 'term') {
      this.terms.waitUntilLoaded().then(() => {
        let term = this.terms.currentTerm;
        if (!isEmpty(term)) {
          this.startDate = term.start;
          this.endDate = term.end;
        }
      });
    }
  }

  @action
  clearDateFilter() {
    this.dateFilter = '';
    this.startDate = '';
    this.endDate = '';
  }

  @action
  setScheduleFilter(value) {
    if (value === this.scheduleFilter) {
      this.scheduleFilter = '';
    } else {
      this.scheduleFilter = value;
    }
  }

  @action
  toggleSelected(log) {
    if (this.selected.includes(log)) {
      this.selected = this.selected.filter((l) => l !== log);
    } else {
      this.selected = [...this.selected, log];
    }
  }

  @action
  toggleAll() {
    if (this.selected.length >= this.filteredLogs.length) {
      // All or more is already selected, so
      // clear selection.
      this.clearSelection();
    } else {
      // Not all selected; select them all.
      this.selected = [...this.filteredLogs]; // Set selected array to a copy of sortedPeople
    }
  }

  @action
  deleteLogs() {
    Promise.all(
      this.selected.map((log) => {
        return log.destroyRecord().catch(() => {
          this.toaster.error('Error occurred while deleting logs');
        });
      }),
    )
      .then(() => {
        this.toaster.success(
          `Successfully deleted ${pluralize(this.selected.length, 'log')}`,
        );
        this.clearSelection();
        this.deleteDialogOpen = false;
      })
      .catch(() => {
        this.toaster.error('Error occurred while deleting logs');
        this.clearSelection();
      });
  }
}
